'use client';
import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/components/language/LanguageProvider';

export default function LocationSection() {
  const revealRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { dictionary } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-[#0A0A0A] py-16 overflow-hidden border-t border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Location */}
          <div
            ref={(el) => {
              revealRefs.current[0] = el;
            }}
            className="reveal bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4 group-hover:bg-primary/18 transition-colors">
              <Icon name="MapPinIcon" size={18} variant="outline" />
            </div>
            <h3 className="text-foreground font-bold text-sm mb-1.5">
              {dictionary.location.location}
            </h3>
            <p className="text-muted-foreground text-sm leading-[1.7]">
              Peñol, Antioquia
              <br />
              Colombia
            </p>
          </div>

          {/* Instagram */}
          <div
            ref={(el) => {
              revealRefs.current[1] = el;
            }}
            className="reveal reveal-delay-1 bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4 group-hover:bg-primary/18 transition-colors">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
              </svg>
            </div>
            <h3 className="text-foreground font-bold text-sm mb-1.5">Instagram</h3>
            <a
              href="https://www.instagram.com/juga_tattoo/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary text-sm font-semibold hover:text-primary/75 transition-colors inline-flex items-center gap-1.5 group/link"
            >
              @juga_tattoo
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-60 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform duration-200"
              >
                <path d="M7 7h10v10M7 17 17 7" />
              </svg>
            </a>
          </div>

          {/* Booking */}
          <div
            ref={(el) => {
              revealRefs.current[2] = el;
            }}
            className="reveal reveal-delay-2 bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4 group-hover:bg-primary/18 transition-colors">
              <Icon name="CalendarDaysIcon" size={18} variant="outline" />
            </div>
            <h3 className="text-foreground font-bold text-sm mb-1.5">
              {dictionary.location.bookings}
            </h3>
            <a
              href="#booking"
              className="text-primary text-sm font-semibold hover:text-primary/75 transition-colors inline-flex items-center gap-1.5 group/link"
            >
              {dictionary.location.bookOnline}
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-60 group-hover/link:translate-x-0.5 transition-transform duration-200"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
